import React, { useState } from 'react';
import axios from 'axios';
import {
    Container,
    TextField,
    Button,
    Typography,
    Box,
    Card,
    CardContent,
    InputAdornment,
    IconButton,
    Alert,
    AlertTitle,
    Snackbar,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { useApp } from '../context/AppContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getApiUrl } from '../utils/apiUrl';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const Login = () => {
    const { setUserFromApi } = useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const fromPath = location.state?.from;
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e?.preventDefault();
        setError('');

        if (!username || !password) {
            setError('กรุณากรอกชื่อผู้ใช้และรหัสผ่าน');
            return;
        }

        setLoading(true);
        try {
            const loginUrl = getApiUrl('/api/user/auth/login/');
            const csrfToken = (typeof document !== 'undefined' && document.cookie)
                ? document.cookie.replace(/(?:(?:^|.*;\s*)csrftoken\s*=\s*([^;]*).*$)|^.*$/, '$1')
                : '';
            const headers = {
                accept: 'application/json',
                'Content-Type': 'application/json',
                ...(csrfToken ? { 'X-CSRFTOKEN': csrfToken } : {}),
            };
            const { data } = await axios.post(loginUrl, { username, password }, {
                withCredentials: true,
                headers,
            });
            setUserFromApi(data);
            navigate(fromPath || '/select-app', { replace: true });
        } catch (err) {
            const msg =
                err?.response?.data?.detail ??
                err?.response?.data?.message ??
                err?.message ??
                'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setLoading(false);
        }
    };


    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(120deg, rgb(175, 227, 251), rgb(241, 195, 248), rgb(250, 236, 190), rgb(185, 224, 242))',
                backgroundSize: '240% 240%',
                padding: 2,
                position: 'relative',
                overflow: 'hidden',
                '@keyframes gradientShift': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                },
                '@keyframes floatSlow': {
                    '0%, 100%': { transform: 'translateX(-50%) translateY(0px)' },
                    '50%': { transform: 'translateX(-50%) translateY(-10px)' },
                },
                '@keyframes cardIn': {
                    '0%': { opacity: 0, transform: 'translateY(16px) scale(0.99)' },
                    '100%': { opacity: 1, transform: 'translateY(0px) scale(1)' },
                },
                '@keyframes iconFloat': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-6px)' },
                },
                animation: 'gradientShift 12s ease-in-out infinite',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: 420,
                    height: 420,
                    borderRadius: '50%',
                    top: -140,
                    left: '15%',
                    background: 'radial-gradient(circle at 30% 30%, rgba(132, 94, 194, 0.18), rgba(132, 94, 194, 0) 60%)',
                    filter: 'blur(2px)',
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: 520,
                    height: 520,
                    borderRadius: '50%',
                    bottom: -200,
                    left: '85%',
                    transform: 'translateX(-50%)',
                    background: 'radial-gradient(circle at 30% 30%, rgba(48, 176, 235, 0.18), rgba(48, 176, 235, 0) 60%)',
                    filter: 'blur(2px)',
                    pointerEvents: 'none',
                },
                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                },
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '200px',
                    height: '1px',
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                    mt: 4,
                    animation: 'floatSlow 4.5s ease-in-out infinite',
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
            />

            <Container maxWidth="xs">
                <Card
                    elevation={0}
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                        borderRadius: 5,
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        animation: 'cardIn 500ms cubic-bezier(0.2, 0.9, 0.2, 1) both',
                        transformOrigin: 'center',
                        transition: 'transform 200ms ease, box-shadow 200ms ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 14px 40px rgba(0, 0, 0, 0.10)',
                        },
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                            transition: 'none',
                            '&:hover': { transform: 'none' },
                        },
                    }}
                >
                    <CardContent sx={{ p: 3, pt: 3 }}>
                        <Box sx={{ textAlign: 'center', mb: 5 }}>
                            <SupportAgentIcon
                                sx={{
                                    fontSize: 70,
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    mb: 1,
                                    animation: 'iconFloat 3.2s ease-in-out infinite',
                                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                                }}
                            />
                            <Typography
                                variant="h6"
                                component="h1"
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    fontWeight: 800,
                                    letterSpacing: 1,
                                    fontSize: '1.2rem',
                                    fontFamily: 'Kanit, sans-serif',
                                    mb: 0.5
                                }}
                            >
                                ASSIST
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    fontWeight: 600,
                                    letterSpacing: 1,
                                    fontSize: '0.8rem',
                                    fontFamily: 'Kanit, sans-serif',
                                    mb: 3
                                }}
                            >
                                ระบบรับแจ้งปัญหา - ข้อขัดข้องของกองทัพอากาศ

                            </Typography>
                        </Box>

                        <Box component="form" onSubmit={handleLogin} >

                            <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 3 }}>
                                <EmailIcon sx={{ color: 'rgba(0, 0, 0, 0.4)', mr: 2, mb: 1, fontSize: 25 }} />
                                <TextField
                                    fullWidth
                                    placeholder="ชื่อผู้ใช้"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    autoComplete="username"
                                    autoFocus
                                    variant="standard"
                                    InputProps={{
                                        disableUnderline: false,
                                    }}
                                    sx={{
                                        transition: 'transform 160ms ease',
                                        '& .MuiInput-input': {
                                            color: 'rgba(0, 0, 0, 0.6)',
                                            fontSize: '0.8rem',
                                            paddingBottom: '8px',
                                        },
                                        '& .MuiInput-underline:before': {
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
                                        },
                                        '& .MuiInput-underline:hover:before': {
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
                                        },
                                        '& .MuiInput-underline:after': {
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.4)',
                                        },
                                        '&:focus-within': {
                                            transform: 'translateY(-1px)',
                                        },
                                        '& .MuiInputBase-root': {
                                            '&:before': {
                                                borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
                                            },
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 3 }}>
                                <LockIcon sx={{ color: 'rgba(0, 0, 0, 0.4)', mr: 2, mb: 1, fontSize: 25 }} />
                                <TextField
                                    fullWidth
                                    placeholder="รหัสผ่าน"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    variant="standard"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                    sx={{ mb: 0.5, color: 'rgba(0, 0, 0, 0.4)', padding: '4px' }}
                                                >
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        disableUnderline: false,
                                    }}
                                    sx={{
                                        transition: 'transform 160ms ease',
                                        '& .MuiInput-input': {
                                            color: 'rgba(0, 0, 0, 0.6)',
                                            fontSize: '0.8rem',
                                            paddingBottom: '8px',
                                        },
                                        '& .MuiInput-underline:before': {
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
                                        },
                                        '& .MuiInput-underline:hover:before': {
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
                                        },
                                        '& .MuiInput-underline:after': {
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.4)',
                                        },
                                        '&:focus-within': {
                                            transform: 'translateY(-1px)',
                                        },
                                        '& .MuiInputBase-root': {
                                            '&:before': {
                                                borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
                                            },
                                        },
                                    }}
                                />
                            </Box>



                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                type="submit"
                                disabled={loading}
                                sx={{
                                    py: 1.5,
                                    mb: 3,
                                    mt: 3,
                                    borderRadius: 1,
                                    bgcolor: '#845EC2',
                                    color: 'white',
                                    textTransform: 'uppercase',
                                    letterSpacing: 1.5,
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    fontFamily: 'Kanit, sans-serif',
                                    boxShadow: '0 10px 20px rgba(132, 94, 194, 0.22)',
                                    transition: 'transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease',
                                    '&:hover': {
                                        bgcolor: '#6344A4',
                                        boxShadow: '0 14px 26px rgba(132, 94, 194, 0.28)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0px) scale(0.99)',
                                        boxShadow: '0 8px 14px rgba(132, 94, 194, 0.22)',
                                    },
                                    '@media (prefers-reduced-motion: reduce)': {
                                        transition: 'none',
                                        '&:hover': { transform: 'none' },
                                        '&:active': { transform: 'none' },
                                    }
                                }}
                            >
                                {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
                            </Button>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    fontWeight: 500,
                                    letterSpacing: 1,
                                    fontSize: '0.8rem',
                                    fontFamily: 'Kanit, sans-serif',
                                    textAlign: 'center'
                                }}
                            >
                                พัฒนาโดยศูนย์ซอฟต์แวร์กองทัพอากาศ

                            </Typography>

                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.8rem',
                                        fontFamily: 'Kanit, sans-serif',
                                    }}
                                >
                                    Version 1.2.20 ( build 45 )
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Container>

            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '200px',
                    height: '1px',
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                    mb: 4,
                    animation: 'floatSlow 4.5s ease-in-out infinite',
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
            />

            {/* Error Snackbar */}
            <Snackbar
                open={!!error}
                autoHideDuration={1500}
                onClose={() => setError('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setError('')}
                    severity="error"
                    sx={{ width: '100%', fontSize: '0.8rem' }}
                >
                    <AlertTitle sx={{ fontSize: '0.8rem' }}>เกิดข้อผิดพลาด</AlertTitle>
                    {error}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default Login;
